'use client';

import { useState, useEffect } from 'react';
import { UserDomain } from '@/lib/services/domain-switching-service';
import { toast } from 'sonner';

interface UseActiveDomainResult {
  activeDomain: UserDomain | null;
  domains: UserDomain[];
  isLoading: boolean;
  switchDomain: (domainId: string) => Promise<void>;
  refreshDomains: () => Promise<void>;
}

export function useActiveDomain(): UseActiveDomainResult {
  const [activeDomain, setActiveDomain] = useState<UserDomain | null>(null);
  const [domains, setDomains] = useState<UserDomain[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDomains = async () => {
    try {
      const response = await fetch('/api/domains/switch');
      if (!response.ok) throw new Error('Failed to fetch domains');
      
      const data = await response.json();
      setDomains(data.domains || []);
      setActiveDomain(data.activeDomain || null);
    } catch (error) {
      console.error('Error fetching domains:', error);
      toast.error('Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  };

  const switchDomain = async (domainId: string) => {
    if (activeDomain?.domain_id === domainId) return;
    
    try {
      const response = await fetch('/api/domains/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domainId }),
      });

      if (!response.ok) throw new Error('Failed to switch domain');
      
      const data = await response.json();
      setActiveDomain(data.activeDomain);
      
      // Update domains list to reflect the change
      setDomains(prev => prev.map(d => ({
        ...d,
        is_active: d.domain_id === domainId,
      })));
      
      toast.success(`Switched to ${data.activeDomain.domain_name}`);
    } catch (error) {
      console.error('Error switching domain:', error);
      toast.error('Failed to switch domain');
      throw error;
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  return {
    activeDomain,
    domains,
    isLoading,
    switchDomain,
    refreshDomains: fetchDomains,
  };
}